import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

import {
  DeleteFilled,
  EditFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { message, Modal, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useHistory, useLocation } from 'react-router-dom';
import { useTableUrlSync } from '../../../../../hooks/useTableUrlSync';

import { ApiError, handleError } from '../../../../../api/base';
import { useAuth } from '../../../../../hooks/useAuth';
import { RoleModel } from '../../../../../models/role';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { deleteRole } from '../../../../../store/features/acl/roles/rolesSlice';
import { formatDate, UserPermissions } from '../../../../../util';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';
import './RoleTableList.less';

const { Text } = Typography;

interface RoleTableListProps {
  roles: RoleModel[];
  selectedRoles?: RoleModel[];
  loading: boolean;
  view?: 'Users' | 'Roles';
  onRolesSelected?: (roles: RoleModel[]) => void;
}

const RoleTableList: React.FC<RoleTableListProps> = ({
  roles,
  selectedRoles = [],
  loading,
  view,
  onRolesSelected,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuth();
  const tableListWrapperEl = useRef<any>();

  const { totalCount, page, pageSize } = useAppSelector((state) => state.roles);
  const [localSort, setLocalSort] = useState<{
    field?: string;
    order?: 'ascend' | 'descend';
  }>({});

  const onError = useCallback((err: ApiError) => {
    message.destroy('deleting-user-message');
    handleError(err);
  }, []);

  const handleDeleteRole = useCallback(
    async (role: RoleModel) => {
      message.loading(
        { content: 'Deleting role...', key: 'deleting-user-message' },
        0
      );

      const res: any = await dispatch(deleteRole(role.id));
      if (res.error) {
        onError({ error: res.payload.error });
      } else {
        message.destroy('deleting-user-message');
        message.success(`Role '${role.display_name}' deleted.`);
      }
    },
    [onError, dispatch]
  );

  const handleConfirmDeleteRole = useCallback(
    (e: MouseEvent, role: RoleModel) => {
      e.preventDefault();
      Modal.confirm({
        title: `Delete role '${role.display_name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover this role.',
        okText: 'Delete',
        onOk: () => handleDeleteRole(role),
      });
    },
    [handleDeleteRole]
  );

  const SORT_FIELD_MAP: Record<string, string> = useMemo(
    () => ({ updated_at: 'updated_at' }),
    []
  );

  const { currentSortField, currentSortOrder, onTableChange } = useTableUrlSync(
    {
      location,
      history,
      page,
      pageSize,
      sortFieldMap: SORT_FIELD_MAP,
    }
  );

  const columns: ColumnsType<RoleModel> = useMemo(() => {
    let cols: any = [
      {
        title: 'Name',
        dataIndex: 'display_name',
        key: 'display_name',
        render: (name: string) => {
          return <Text>{name}</Text>;
        },
        // client-side sorting only (not URL-driven)
        sorter: true,
        sortOrder: localSort.field === 'display_name' ? localSort.order : null,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description: string) =>
          !!description ? (
            <Tooltip title={description} mouseEnterDelay={1}>
              <Text
                ellipsis
                style={{ maxWidth: '400px' }}
                className={view === 'Roles' ? 'column-size' : ''}
              >
                {description}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              No description.
            </Text>
          ),
        // no sorting for description
      },
    ];

    if (view === 'Roles') {
      cols = [
        ...cols,
        {
          title: 'Last updated',
          dataIndex: 'updated_at',
          key: 'updated_at',
          render: (date: string) => {
            return <div>{date ? formatDate(date) : null}</div>;
          },
          sorter: true,
          sortOrder:
            currentSortField === 'updated_at' ? currentSortOrder : null,
        },
        {
          title: 'Actions',
          key: 'actions',
          align: 'right',
          render: (role: RoleModel) => (
            <Space direction="horizontal" size="middle">
              {hasPermission(UserPermissions.RolesEdit) && (
                <Tooltip title="Edit">
                  <EditFilled
                    onClick={() => {
                      history.push({
                        hash: DrawerHashRoute.RoleForm,
                        state: { data: role },
                      });
                    }}
                    className="roles-table-list-action-icon"
                  />
                </Tooltip>
              )}
              {hasPermission(UserPermissions.RolesDelete) && (
                <Tooltip title="Delete">
                  <DeleteFilled
                    onClick={(e) => handleConfirmDeleteRole(e, role)}
                    className="roles-table-list-action-icon"
                  />
                </Tooltip>
              )}
            </Space>
          ),
        },
      ];
    }

    return cols;
  }, [
    view,
    history,
    handleConfirmDeleteRole,
    hasPermission,
    currentSortField,
    currentSortOrder,
    localSort.field,
    localSort.order,
  ]);

  const displayedRoles = useMemo(() => {
    if (localSort.field === 'display_name' && localSort.order) {
      const sorted = [...roles].sort((a, b) => {
        const da = (a.display_name || '').toLowerCase();
        const db = (b.display_name || '').toLowerCase();
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
      return localSort.order === 'ascend' ? sorted : sorted.reverse();
    }
    return roles;
  }, [roles, localSort]);

  const handleTableChange = useCallback(
    (
      pagination: { current?: number; pageSize?: number },
      filters: any,
      sorter: any
    ) => {
      const primarySorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const fieldKey =
        (primarySorter?.field as string) ||
        primarySorter?.column?.dataIndex ||
        undefined;
      if (fieldKey === 'display_name') {
        setLocalSort({ field: 'display_name', order: primarySorter?.order });
      } else if (primarySorter?.order == null) {
        setLocalSort({});
      }
      onTableChange(pagination, filters, sorter);
    },
    [onTableChange]
  );

  return (
    <div ref={tableListWrapperEl}>
      <Table
        onChange={handleTableChange}
        rowSelection={
          view === 'Users'
            ? {
                type: 'checkbox',
                onChange: (_: React.Key[], selectedRows: RoleModel[]) => {
                  onRolesSelected?.(selectedRows);
                },
                selectedRowKeys: selectedRoles.map(
                  (role) => `${role.id}-row-key`
                ),
              }
            : undefined
        }
        loading={loading && roles.length === 0}
        locale={{ emptyText: <></> }}
        rowKey={(role) => `${role.id}-row-key`}
        sortDirections={['ascend', 'descend', 'ascend']}
        className="roles-table-list"
        pagination={
          view === 'Roles'
            ? {
                current: page,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
              }
            : false
        }
        columns={columns}
        dataSource={displayedRoles}
      />

      {loading && roles.length > 0 && (
        <div className="loading-more-spin-wrapper">
          <Spin />
        </div>
      )}
    </div>
  );
};

export default RoleTableList;
